/**
 * "Retire orphaned task" -- the console control for the
 * `orphaned-task-retire` runbook, which until 2026-09-15 said "there is
 * no console control for this anywhere -- this text is the whole
 * procedure". The procedure was a hand-built POST with a bearer token,
 * during an incident, against the admin API.
 *
 * WHERE IT LIVES. This console has no per-site task view (the operator
 * SPA owns tasks; this app talks to `/support/**`, which mounts no task
 * routes). The nearest thing a support engineer has to "the place for
 * this failure" is the runbook's own card in Act, which Diagnose already
 * pins and scrolls to from a `task_stalled` issue. So the control is
 * rendered inside that card, against the pane's one target site.
 *
 * WHO SEES IT. `nexsoss:platform_admin` and nobody else, and for
 * everyone else it renders NOTHING -- not a disabled button. That is the
 * endpoint's own rule (`admin.py::require_platform_admin`, edge-authz's
 * `_ADMIN_ROLES`, and `task.retire` in the capability table held by
 * platform_admin alone), repeated here as UX rather than as the boundary.
 * support_l3 runs every l3 runbook in this pane and still cannot retire:
 * retiring rewrites the record of what a robot did, which is a different
 * kind of act from restarting a container, and the backend says so in a
 * test that pins the holder set. Widening it here would only produce a
 * button that 403s.
 *
 * HOW IT REACHES THE ADMIN ROUTE. Through the same `apiBaseUrl` as every
 * other call: that is the Envoy front door, which routes `/api/v1/admin/`
 * to `nexsoss-api-server-admin` behind edge-authz's platform_admin check.
 * Nothing is proxied through the support container, so the support
 * process still never imports the admin router.
 *
 * WHAT IT SHOWS BEFORE THE BUTTON is the runbook's step 1, as facts
 * rather than a verdict: state and when the key last changed, the
 * record's projector epoch against the live worker's, the task controls
 * this platform sent and what became of them, and the assigned robot's
 * own projected state -- the one thing the confirm step's "no robot is
 * executing this task" tick can be checked against. Point (4), the
 * adapter's "before receiving BidNotice" log line, is a search the
 * engineer runs; an empty answer is stated as not evidence.
 *
 * TWO READINGS THIS COPY REFUSES TO MAKE, both from review:
 *
 * - "No record" is never "nothing there". A wedged storage manager can
 *   answer with nothing, and "check the task id" during a wedge is the
 *   push toward the adapter restart that makes orphans. The backend
 *   reports an error-or-undecodable answer as `bus_available: false`;
 *   an answer of zero replies is worded here as "no record returned".
 * - A matching epoch is not "probably a slow task". Point (2) only holds
 *   when the WORKER restarted after the task's last write. The case this
 *   was built for, `compose.dispatch-2585e2d74e`, was dropped by a
 *   fleet-adapter-only restart about 94 minutes after worker 0.4.27
 *   started, so its key can carry the live worker's epoch and still be
 *   an orphan. A match therefore defers to points 1, 3 and 4.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusChip } from '@shared/design-system/components';
import { useAuth } from '@shared/auth/useAuth';
import {
  getOrphanTaskEvidence,
  getSupportLogs,
  retireOrphanedTask,
  type OrphanTaskEvidence,
  type RetiredTask,
} from '../../api/supportClient';
import {
  RETIRE_REASON_MAX,
  TASK_ID_RE,
  canRetireTasks,
  describeRetireError,
  errorText,
  formatAge,
  isRetireReasonValid,
  retireReasonLength,
} from './retire-task-rules';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 4 }}>
      <span style={{ color: 'var(--nx-muted)', minWidth: 150 }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function EpochVerdict({ ev }: { ev: OrphanTaskEvidence }) {
  if (ev.epoch_matches_live_worker === false) {
    return (
      <span data-testid="retire-epoch-verdict" style={{ color: 'var(--nx-warning)' }}>
        Differs — the key was written by a worker generation that is no longer running (runbook
        point 2). Nothing running has touched this record since the worker restarted.
      </span>
    );
  }
  if (ev.epoch_matches_live_worker === true) {
    return (
      <span data-testid="retire-epoch-verdict" style={{ color: 'var(--nx-muted)' }}>
        Matches the live worker. That decides nothing on its own: the epoch changes only when the
        site worker restarts, and a fleet-adapter-only restart — the usual way a task is orphaned —
        leaves it unchanged. Point 2 does not apply; points 1, 3 and 4 decide.
      </span>
    );
  }
  return (
    <span data-testid="retire-epoch-verdict" style={{ color: 'var(--nx-muted)' }}>
      Cannot compare: one of the two epochs is unknown.
    </span>
  );
}

/** What the assigned robot's own projected state says it is doing --
 *  the only platform fact behind "no robot is executing this task".
 *  Shown in the evidence and again beside the tick box. */
function RobotNow({ ev }: { ev: OrphanTaskEvidence }) {
  const robot = ev.assigned_robot ?? 'the robot';
  if (!ev.robot_state_available) {
    return (
      <span data-testid="retire-robot-now" style={{ color: 'var(--nx-muted)' }}>
        {robot}'s state could not be read. Check it on the operator console before ticking.
      </span>
    );
  }
  const age =
    ev.robot_state_at_ms != null ? ` (state ${formatAge(Date.now() - ev.robot_state_at_ms)} old)` : '';
  if (ev.robot_executing_this_task) {
    return (
      <strong data-testid="retire-robot-now" role="alert" style={{ color: 'var(--nx-error)' }}>
        {robot} is currently on this task{age}. Its own state still names it, so this is not an
        orphan unless that state is stale. Do not retire.
      </strong>
    );
  }
  return (
    <span data-testid="retire-robot-now">
      {robot} is currently on {ev.robot_task_id ? <code>{ev.robot_task_id}</code> : 'no task'}
      {age}.
    </span>
  );
}

function BidNoticeLogCheck({ site, ev }: { site: string; ev: OrphanTaskEvidence }) {
  const [asked, setAsked] = useState(false);
  // The id already passed the server's key-segment guard to get here, so
  // it holds no quote or backslash that could escape the LogQL string.
  const logql = `{site=~".+"} |= "before receiving BidNotice" |= "${ev.task_id}"`;
  const logs = useQuery({
    queryKey: ['support', 'orphan-bidnotice-logs', site, ev.task_id],
    queryFn: () =>
      getSupportLogs({
        site,
        query: logql,
        since_ms: ev.projected_at_ms ?? undefined,
        limit: 20,
      }),
    enabled: asked,
    retry: false,
  });
  return (
    <div style={{ marginTop: 8, fontSize: 13 }}>
      {/* The platform log search goes through the support api-server's
          LOKI_URL, which is still `http://loki:3100` -- the in-cluster Loki
          that stopped receiving writes when both collectors moved to the
          observability VM on 2026-09-05. It answers empty, not with an
          error, so until LOKI_URL points at the live store this button
          cannot find a post-cutover line and the adapter's own log is the
          real check. Said up front so an empty answer is not read as a
          negative. */}
      <div style={{ color: 'var(--nx-muted)' }}>
        Point 4: the fleet adapter logs “before receiving BidNotice. This request will be ignored.”
        for a task it was awarded but never bid on. Confirm it in the fleet adapter's own log: the
        platform log search below cannot currently see lines written after 2026-09-05.
      </div>
      {!asked ? (
        <button type="button" onClick={() => setAsked(true)} style={{ marginTop: 4 }}>
          Search logs for the BidNotice line
        </button>
      ) : logs.isPending ? (
        <div>Searching…</div>
      ) : logs.isError ? (
        <div style={{ color: 'var(--nx-error)' }}>Log search failed: {errorText(logs.error)}</div>
      ) : logs.data.lines.length === 0 ? (
        <div data-testid="retire-log-empty" style={{ color: 'var(--nx-muted)' }}>
          No lines returned. That is not evidence either way. The log store is not guaranteed to
          hold this site's lines for that window, and as of 2026-09-15 this search reads the
          in-cluster Loki, which stopped receiving lines at the 2026-09-05 observability cutover. Check
          the fleet adapter's own log for the line instead.
        </div>
      ) : (
        <ul data-testid="retire-log-lines" style={{ margin: '4px 0', paddingLeft: 18 }}>
          {logs.data.lines.map((l, i) => (
            <li key={i}>
              <code>{new Date(l.at_ms).toLocaleString()}</code> {l.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EvidenceView({ site, ev }: { site: string; ev: OrphanTaskEvidence }) {
  const now = Date.now();
  if (!ev.bus_available) {
    return (
      <p data-testid="retire-evidence-bus-unavailable" style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
        Could not read this task's projected record ({ev.bus_error ?? 'platform bus unavailable'}).
        Its state is UNKNOWN, not absent, and a retire would be refused the same way until the bus
        is reachable.
      </p>
    );
  }
  if (!ev.projected) {
    return (
      <p data-testid="retire-evidence-not-projected" style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
        The platform bus returned no record for <code>{ev.task_id}</code> at this site
        {ev.bus_replies === 0 ? ' (no replies at all)' : ''}, so a retire would be refused. That is
        not proof the task is gone: a wrong id looks like this, and so does a storage manager that
        has stopped answering. If the id is right and the operator console still shows the task
        active, suspect the platform bus, not the task — and do not restart the fleet adapter.
      </p>
    );
  }
  return (
    <div data-testid="retire-evidence" style={{ marginTop: 8 }}>
      <Row label="State">
        <StatusChip status={ev.terminal ? 'offline' : 'warning'} label={ev.state ?? 'unknown'} />
        {ev.terminal && (
          <span style={{ color: 'var(--nx-error)', marginLeft: 8 }}>
            Already ended. The endpoint refuses to retire a finished task.
          </span>
        )}
      </Row>
      <Row label="Last changed">
        {ev.projected_at_ms != null ? (
          <span data-testid="retire-last-changed">
            {new Date(ev.projected_at_ms).toLocaleString()} ({formatAge(now - ev.projected_at_ms)}{' '}
            ago)
          </span>
        ) : (
          'unknown'
        )}
      </Row>
      <Row label="Assigned">
        {ev.assigned_robot ?? 'no robot'}
        {ev.assigned_fleet ? ` (${ev.assigned_fleet})` : ''}
      </Row>
      {ev.assigned_robot && (
        <Row label="Robot is now on">
          <RobotNow ev={ev} />
        </Row>
      )}
      <Row label="Projector epoch">
        <span data-testid="retire-epochs">
          record {ev.projector_epoch ?? 'unknown'} · live worker {ev.worker_epoch ?? 'unknown'} (
          {ev.worker_status})
        </span>
      </Row>
      <Row label="">
        <EpochVerdict ev={ev} />
      </Row>
      <div style={{ fontSize: 13, marginTop: 8 }}>
        <div style={{ color: 'var(--nx-muted)' }}>
          Task controls sent from this platform (point 3 is a cancel answered outcome_unknown):
        </div>
        {ev.recent_controls.length === 0 ? (
          <div data-testid="retire-no-controls" style={{ color: 'var(--nx-muted)' }}>
            None on file. Not evidence either way — a cancel sent some other way is not recorded
            here.
          </div>
        ) : (
          <ul data-testid="retire-controls" style={{ margin: '4px 0', paddingLeft: 18 }}>
            {ev.recent_controls.map((c) => (
              <li key={c.command_id}>
                {c.capability}{' '}
                <StatusChip
                  status={c.state === 'outcome_unknown' ? 'warning' : c.state === 'rejected' ? 'error' : 'idle'}
                  label={c.state}
                />{' '}
                {new Date(c.submitted_at_ms).toLocaleString()}
                {c.outcome_message ? ` — ${c.outcome_message}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
      <BidNoticeLogCheck site={site} ev={ev} />
    </div>
  );
}

function RetiredView({ result }: { result: RetiredTask }) {
  const failureReason =
    typeof result.record?.failure_reason === 'string' ? result.record.failure_reason : null;
  return (
    <div
      role="status"
      data-testid="retire-result"
      style={{ border: '1px solid var(--nx-cyan)', borderRadius: 'var(--nx-r)', padding: 10, marginTop: 8, fontSize: 13 }}
    >
      <strong>
        Retired <code>{result.task_id}</code> at {result.site_id}.
      </strong>{' '}
      It was <code>{result.previous_state}</code>
      {result.previous_projector_epoch != null ? ` under projector epoch ${result.previous_projector_epoch}` : ''}
      {result.previous_projected_at_ms != null
        ? `, last projected ${new Date(result.previous_projected_at_ms).toLocaleString()}`
        : ''}
      . The platform now records it as failed{failureReason ? `: ${failureReason}` : ''}. Audited as{' '}
      {result.retired_by ?? 'your account'}.
      <div style={{ marginTop: 6 }}>
        Next: hard-reload the operator console (a soft refresh can re-serve the cached task list and
        make it look as if this failed), then re-dispatch the stranded work — retiring does not
        re-run it.
      </div>
    </div>
  );
}

function RetireOrphanedTaskControl({ site, siteLabel }: { site: string; siteLabel: string }) {
  const qc = useQueryClient();
  const [taskInput, setTaskInput] = useState('');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<RetiredTask | null>(null);

  // A different target site is a different task. Everything checked,
  // typed or confirmed against the old one is dropped rather than
  // silently re-aimed.
  useEffect(() => {
    setTaskId(null);
    setConfirming(false);
    setReason('');
    setAcknowledged(false);
    setResult(null);
  }, [site]);

  const evidenceKey = ['support', 'orphan-evidence', site, taskId] as const;
  const evidence = useQuery({
    queryKey: evidenceKey,
    queryFn: () => getOrphanTaskEvidence(site, taskId as string),
    enabled: Boolean(site && taskId),
    retry: false,
  });

  const retire = useMutation({
    // `reason` exactly as typed. It is the audit row's "why".
    mutationFn: () => retireOrphanedTask(site, taskId as string, reason),
    onSuccess: (resp) => {
      setResult(resp);
      setConfirming(false);
      // The reason was THIS task's "why". Left in place it pre-fills the
      // next task's audit row, and only the tick box would be re-asked.
      setReason('');
      setAcknowledged(false);
      void qc.invalidateQueries({ queryKey: evidenceKey });
    },
  });

  const trimmedInput = taskInput.trim();
  const inputValid = TASK_ID_RE.test(trimmedInput);
  const ev = evidence.data;
  // Refuse to OPEN the confirm step only where the answer is already
  // known to be a refusal. A failed evidence read does not block: the
  // confirm step then says the checks were not shown, and the server
  // still holds every precondition.
  //
  // AND A HELD TASK (WS3). `terminal` was the whole test until 0.6.35,
  // and it is no longer sufficient in either direction: an attention task
  // is `active` with a robot actually driving it, and a record that reads
  // terminal can still have a robot on it. Retiring either would write a
  // synthetic ending over work that is still happening. The api-server
  // refuses it with 409 `task_still_held_by_robot`; this is the console
  // half, and it now has an alternative to point at.
  const knownRefusal = Boolean(
    ev && (ev.terminal || (ev.bus_available && !ev.projected) || ev.held === 'held'),
  );
  const robotName = ev?.assigned_robot ?? null;

  if (!site) {
    return (
      <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
        Choose a target site above to retire an orphaned task there.
      </p>
    );
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!inputValid) return;
          setTaskId(trimmedInput);
          setConfirming(false);
          setReason('');
          setAcknowledged(false);
          setResult(null);
          retire.reset();
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <label htmlFor="retire-task-id" style={{ fontSize: 13 }}>
          Task id at {siteLabel || site}
        </label>
        <input
          id="retire-task-id"
          value={taskInput}
          onChange={(e) => setTaskInput(e.target.value)}
          placeholder="compose.dispatch-2585e2d74e"
          spellCheck={false}
        />
        <button type="submit" disabled={!inputValid}>
          Check evidence
        </button>
        {trimmedInput && !inputValid && (
          <span style={{ color: 'var(--nx-warning)', fontSize: 12 }}>
            Not a plain RMF task id (letters, digits, <code>. _ : -</code>).
          </span>
        )}
      </form>

      {taskId && evidence.isPending && <p style={{ fontSize: 13 }}>Reading evidence…</p>}
      {taskId && evidence.isError && (
        <p data-testid="retire-evidence-error" style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Could not read the evidence ({errorText(evidence.error)}). You can still retire, but you
          will be doing it without the checks the runbook asks for.
        </p>
      )}
      {taskId && ev && <EvidenceView site={site} ev={ev} />}

      {result && <RetiredView result={result} />}

      {taskId && ev?.held === 'held' && (
        <p data-testid="retire-still-held" style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Still held by {ev.assigned_robot ?? 'a robot'}; use End task held by RMF.
        </p>
      )}

      {taskId && (ev || evidence.isError) && !confirming && !result && (
        <button
          type="button"
          style={{ marginTop: 8 }}
          disabled={knownRefusal}
          onClick={() => setConfirming(true)}
        >
          Retire orphaned task…
        </button>
      )}

      {taskId && confirming && (
        <div
          role="group"
          aria-label="Confirm retire"
          style={{
            border: '1px solid var(--nx-error)',
            borderRadius: 'var(--nx-r)',
            padding: 12,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <strong>
            Retire <code>{taskId}</code> at {siteLabel || site}?
          </strong>
          <p data-testid="retire-consequence" style={{ margin: 0, fontSize: 13 }}>
            Retiring destroys the only record of what this task was doing: its live state is
            replaced by a synthetic failed record, and that cannot be undone. It must not be used on
            a task a robot is executing — if {robotName ?? 'a robot'} is still running it, the robot
            carries on with work the platform has forgotten. It does not re-run the task.
          </p>
          {evidence.isError && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--nx-warning)' }}>
              The evidence above could not be read, so none of the runbook's checks are shown.
            </p>
          )}
          <label htmlFor="retire-reason" style={{ fontSize: 13 }}>
            Reason (required, recorded verbatim in the audit trail)
          </label>
          <textarea
            id="retire-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="orphaned key: adapter epoch N no longer live, cancel answered outcome_unknown"
          />
          <span
            style={{
              fontSize: 12,
              color:
                retireReasonLength(reason) > RETIRE_REASON_MAX ? 'var(--nx-error)' : 'var(--nx-muted)',
            }}
          >
            {retireReasonLength(reason)}/{RETIRE_REASON_MAX}
          </span>
          <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            No robot is executing this task.
          </label>
          {ev?.assigned_robot && <RobotNow ev={ev} />}
          {retire.isError && (
            <div role="alert" data-testid="retire-error" style={{ color: 'var(--nx-error)', fontSize: 13 }}>
              {describeRetireError(retire.error)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={!isRetireReasonValid(reason) || !acknowledged || retire.isPending}
              onClick={() => retire.mutate()}
            >
              Retire task
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                retire.reset();
              }}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function RetireOrphanedTask({ site, siteLabel }: { site: string; siteLabel: string }) {
  const { roles } = useAuth();
  // Nothing at all for any other role -- see the file comment.
  if (!canRetireTasks(roles)) return null;
  return (
    <section
      data-testid="retire-orphaned-task"
      style={{ borderTop: '1px solid var(--nx-border)', marginTop: 12, paddingTop: 12 }}
    >
      <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Retire orphaned task</h3>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--nx-muted)' }}>
        platform_admin only. Calls <code>POST /api/v1/admin/sites/…/tasks/…/retire</code>, audited
        under your name.
      </p>
      <RetireOrphanedTaskControl site={site} siteLabel={siteLabel} />
    </section>
  );
}
